<template>
  <div class="text-input-wrapper">
    <v-text-field
      v-if="mask"
      ref="input"
      v-model="internalValue"
      :type="type"
      :placeholder="placeholder"
      :suffix="suffix"
      :prefix="prefix"
      :append-icon="appendIcon"
      :prepend-icon="prependIcon"
      :class="['text-input', alignClass, { 'text-input--error': hasError }]"
      :style="customStyle"
      :name="name"
      :maxlength="maxLength"
      :autocomplete="autocomplete"
      :readonly="computedReadonly"
      solo
      flat
      single-line
      hide-details
      :height="height"
      v-bind="$attrs"
      v-on="$listeners"
      v-mask="mask"
      @focus="handleFocus"
      @input="handleInput" />
    <v-text-field
      v-else
      ref="input"
      v-model="internalValue"
      :type="type"
      :placeholder="placeholder"
      :suffix="suffix"
      :prefix="prefix"
      :append-icon="appendIcon"
      :prepend-icon="prependIcon"
      :class="['text-input', alignClass, { 'text-input--error': hasError }]"
      :style="customStyle"
      :name="name"
      :maxlength="maxLength"
      :autocomplete="autocomplete"
      :readonly="computedReadonly"
      solo
      flat
      single-line
      hide-details
      :height="height"
      v-bind="$attrs"
      v-on="$listeners"
      @focus="handleFocus"
      @input="handleInput" />

    <!-- 에러 메시지 -->
    <div v-if="hasError" class="text-input-error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { mask } from 'vue-the-mask';

export default {
  name: 'TextInput',

  inheritAttrs: false,

  directives: {
    mask,
  },

  props: {
    // v-model
    value: {
      type: [String, Number],
      default: '',
    },

    // 타입: 'text', 'email', 'number', 'tel' 등
    type: {
      type: String,
      default: 'text',
    },

    // placeholder
    placeholder: {
      type: String,
      default: '',
    },

    // 우측 텍스트
    suffix: {
      type: String,
      default: '',
    },

    // 좌측 텍스트
    prefix: {
      type: String,
      default: '',
    },

    // 우측 아이콘
    appendIcon: {
      type: String,
      default: '',
    },

    // 좌측 아이콘
    prependIcon: {
      type: String,
      default: '',
    },

    // 텍스트 정렬: 'left', 'right'
    textAlign: {
      type: String,
      default: 'left',
      validator: value => ['left', 'right'].includes(value),
    },

    // 폰트 크기 (숫자만, px 자동 적용)
    fontSize: {
      type: Number,
      default: 16,
    },

    // 높이
    height: {
      type: [String, Number],
      default: '48',
    },

    // input name
    name: {
      type: String,
      default: '',
    },

    // 최대 길이
    maxLength: {
      type: [String, Number],
      default: null,
    },

    // 에러 메시지
    error: {
      type: String,
      default: '',
    },

    // 숫자 포맷팅 (쉼표 구분)
    formatted: {
      type: Boolean,
      default: false,
    },

    // 입력 마스크 (예: '####-####-####-####', '##/##')
    mask: {
      type: String,
      default: '',
    },

    // autocomplete 속성
    autocomplete: {
      type: String,
      default: 'off',
    },

    // 자동완성 방지 (readonly 트릭 사용)
    preventAutofill: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isReadonly: true,
    };
  },

  computed: {
    internalValue: {
      get() {
        if (this.formatted && this.value) {
          // 숫자를 쉼표로 포맷팅
          const numValue = typeof this.value === 'string' ? this.value.replace(/,/g, '') : this.value;
          return Number(numValue).toLocaleString('ko-KR');
        }
        return this.value;
      },
      set(val) {
        if (this.formatted) {
          // 쉼표 제거 후 숫자만 추출
          const numericValue = val.replace(/[^\d]/g, '');
          this.$emit('input', numericValue ? Number(numericValue) : '');
        } else {
          this.$emit('input', val);
        }
      },
    },

    alignClass() {
      return `text-align-${this.textAlign}`;
    },

    customStyle() {
      return {
        '--input-font-size': `${this.fontSize}px`,
      };
    },

    hasError() {
      return !!this.error;
    },

    computedReadonly() {
      return this.preventAutofill && this.isReadonly;
    },
  },

  mounted() {
    if (this.preventAutofill) {
      this.$nextTick(() => {
        setTimeout(() => {
          this.isReadonly = false;
        }, 100);
      });
    }
  },

  methods: {
    handleFocus() {
      if (this.preventAutofill && this.isReadonly) {
        this.isReadonly = false;
      }
    },

    handleInput(val) {
      if (this.formatted && val) {
        // 숫자와 쉼표만 허용
        const filtered = val.replace(/[^\d,]/g, '');
        if (filtered !== val) {
          this.$nextTick(() => {
            this.internalValue = filtered;
          });
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* TextInput Wrapper */
.text-input-wrapper {
  width: 100%;
}
</style>

<style lang="scss">
/* TextInput 스타일 (scoped 없이 전역 적용) */

.text-input.v-text-field {
  width: 100%;

  /* v-input__control과 v-input__slot 모두에 border 적용 */
  .v-input__control {
    min-height: auto !important;
  }

  .v-input__slot {
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 12px 16px !important;
    gap: 8px !important;
    height: 48px !important;
    min-height: 48px !important;
    background: #ffffff !important;
    border: 1px solid #dbdbdb !important;
    border-radius: 8px !important;
    margin-bottom: 0 !important;
    box-shadow: none !important;
  }

  /* solo + flat 조합에서 생성되는 fieldset 제거 */
  fieldset {
    border: none !important;
  }

  /* focus 상태 (에러가 아닐 때만) */
  &.v-input--is-focused:not(.text-input--error) {
    .v-input__slot {
      border-color: #3a3a3a !important;
      color: var(--default, #3a3a3a) !important;
    }
  }

  input {
    font-family: 'Spoqa Han Sans Neo';
    font-style: normal;
    font-weight: 400;
    font-size: 14px !important;
    line-height: 18px;
    color: #3a3a3a !important;
    padding: 0 !important;
  }

  input::placeholder {
    font-family: 'Spoqa Han Sans Neo';
    font-style: normal;
    font-weight: 400;
    font-size: 14px !important;
    color: #dbdbdb !important;
  }

  .v-input__append-inner,
  .v-input__prepend-inner {
    margin-top: 0 !important;
    align-self: center !important;
  }

  .v-text-field__suffix {
    padding: 4px 6px 2px 6px !important;
  }
}

/* 에러 상태 (focus 여부와 관계없이 항상 빨간색) */
.text-input--error.v-text-field {
  .v-input__slot {
    border-color: #ea2a3a !important;
  }

  /* focus 상태에서도 빨간색 유지 */
  &.v-input--is-focused {
    .v-input__slot {
      border-color: var(--primary_light, #ea2a3a) !important;
    }
  }
}

/* 에러 메시지 */
.text-input-error {
  margin-top: 4px;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  line-height: 16px;
  color: var(--primary_light, #ea2a3a) !important;
}

/* 텍스트 정렬 */
.text-input.text-align-left input {
  text-align: left;
}

.text-input.text-align-right input {
  text-align: right;
}

.text-input.text-align-center input {
  text-align: center;
}
</style>
